import { useEffect, useRef, useState } from 'react';

import {
  DESKTOP_MEDIA_QUERY,
  useIsDesktopViewport,
} from '@OnArk/utils/useIsDesktopViewport';

export type SidebarState = 'OPEN' | 'CONDENSED';

const isSidebarState = (value: unknown): value is SidebarState =>
  value === 'OPEN' || value === 'CONDENSED';

/**
 * Persistent sidebar open/condensed state, with a desktop-friendly default
 * for first-time users and a hard override that forces CONDENSED on
 * viewports below the md breakpoint.
 *
 * Both the server-rendered HTML and the first client render use 'CONDENSED'
 * so hydration always matches. After mount we read the persisted preference
 * from localStorage (if any), upgrade to 'OPEN' on desktop viewports for
 * users who have never toggled before, and watch the viewport so a resize
 * down to mobile width collapses the sidebar even if the user prefers OPEN.
 *
 * The setter still writes to localStorage so the user's desktop preference
 * is preserved when they return to a wider viewport.
 */
export function useSidebarState(
  key: string
): [SidebarState, (state: SidebarState) => void] {
  const [stored, setStored] = useState<SidebarState>('CONDENSED');
  // Viewport tracking (incl. the resize listener) lives in the shared hook.
  const isDesktop = useIsDesktopViewport();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    if (typeof window === 'undefined') return;

    let raw: string | null = null;

    try {
      raw = window.localStorage.getItem(key);
    } catch {
      // localStorage may throw in private browsing or sandboxed contexts.
    }

    if (raw !== null) {
      try {
        const parsed: unknown = JSON.parse(raw);

        if (isSidebarState(parsed)) {
          setStored(parsed);

          return;
        }
      } catch {
        // Fall through to default below.
      }
    }

    // No stored preference — default to OPEN on desktop viewports. Read the
    // media query directly rather than the hook's state: the hook resolves in
    // its own effect, so `isDesktop` is still false on this pass.
    if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      setStored('OPEN');
    }
  }, [key]);

  const persistingSetState = (next: SidebarState) => {
    setStored(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Same private-browsing caveat as above.
    }
  };

  // Below the md breakpoint the rail is too narrow to render an
  // open sidebar usefully — force CONDENSED so child components
  // pick up the right rendering (icon-only buttons, hidden labels,
  // etc.) and don't just inherit a CSS width override.
  const effective: SidebarState = isDesktop ? stored : 'CONDENSED';

  return [effective, persistingSetState];
}
